import { useEffect, useMemo, useRef, useState } from "react";
import type { DecisionDrillConfig } from "../scenario/types";
import { audio } from "../lib/audio";
import { newShuffleSeed, shuffleOptionSets } from "../lib/shuffle";
import { activeTtsLang } from "../lib/locale";
import { tx, txr } from "../lib/tx";

export interface DecisionDrillResult {
  correct: number;
  total: number;
  passed: boolean;
}

/**
 * Short end-of-briefing game: realistic workplace situations, immediate
 * feedback, then a score. Spec-driven so the same component can be reused.
 */
export function DecisionDrill({
  config,
  onComplete,
}: {
  config: DecisionDrillConfig;
  onComplete: (result: DecisionDrillResult) => void;
}) {
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState<"ask" | "feedback" | "score">("ask");
  const [picked, setPicked] = useState<string | null>(null);
  const [correctCount, setCorrectCount] = useState(0);
  const mounted = useRef(true);
  const round = config.rounds[idx];
  // Answers are shown in a shuffled order (display only), fixed for this run of the drill.
  const [shuffleSeed] = useState(newShuffleSeed);
  const orderedOptions = useMemo(
    () => shuffleOptionSets(config.rounds.map((r) => r.options), shuffleSeed),
    [config.rounds, shuffleSeed],
  );
  const voice = config.voiceId ?? "coral";
  const lang = config.language ?? activeTtsLang();
  const instructions =
    "Speak as a calm security-awareness instructor. Use plain language and a concise tone.";
  const passRatio = config.passRatio ?? 0.66;

  useEffect(() => {
    mounted.current = true;
    config.rounds.forEach((item) => void audio.prefetchSpeech(item.narration, { voice, lang, instructions }));
    return () => {
      mounted.current = false;
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase !== "ask" || !round) return;
    let alive = true;
    void (async () => {
      await audio.speak(round.narration, { voice, lang, instructions });
    })();
    return () => {
      alive = false;
      if (!alive) audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, idx]);

  const total = config.rounds.length;
  const chosen = round?.options.find((option) => option.id === picked);
  const answer = round?.options.find((option) => option.correct);

  const finish = () => {
    audio.cancelSpeech();
    onComplete({
      correct: correctCount,
      total,
      passed: total > 0 && correctCount / total >= passRatio,
    });
  };

  return (
    <div className="terminal ddrill-host">
      <div className="term-bezel">
        <div className="term-screen term-screen--kiosk ddrill-screen">
          <div className="term-head">
            <div className="term-head-status">
              <span className="term-head-tag term-head-tag--warn">{tx("DECISION DRILL")}</span>
              <span className="term-head-kicker">
                {phase === "score" ? tx("RESULT") : tx("{v} OF {total}", { v: idx + 1, total })}
              </span>
            </div>
            <div className="term-head-top">
              <span className="term-head-badge" aria-hidden>{phase === "score" ? correctCount : idx + 1}</span>
              <span className="term-head-title">{config.title}</span>
            </div>
            <div className="term-head-rule" aria-hidden />
          </div>

          <div className="ddrill-body">
            {phase === "score" ? (
              <>
                <p className="ddrill-lead">
                  {txr("You chose the safer action in {correctCount} of {total} situations.", { correctCount, total })}
                </p>
                <p className="ddrill-note">
                  {correctCount / total >= passRatio
                    ? tx("That is the habit this briefing is for: pause, then pick the action that protects the information.")
                    : tx("The safer action is usually the one that checks purpose, recipient and approval before the information moves.")}
                </p>
              </>
            ) : (
              <>
                <p className="ddrill-lead">{round?.prompt}</p>
                <p className="ddrill-cue">{tx("Click one answer below.")}</p>
                <div className="ddrill-options">
                  {(orderedOptions[idx] ?? []).map((option, index) => {
                    const state =
                      phase === "feedback" && option.id === picked
                        ? option.correct ? "is-correct" : "is-wrong"
                        : phase === "feedback" && option.correct
                          ? "is-answer"
                          : "";
                    return (
                      <button
                        key={option.id}
                        type="button"
                        className={`ddrill-opt ${state}`}
                        disabled={phase === "feedback"}
                        onClick={() => {
                          if (phase !== "ask") return;
                          audio.cancelSpeech();
                          if (option.correct) setCorrectCount((value) => value + 1);
                          setPicked(option.id);
                          setPhase("feedback");
                        }}
                      >
                        <span className="ddrill-opt-mark" aria-hidden>{String.fromCharCode(65 + index)}</span>
                        <span className="ddrill-opt-text">{option.label}</span>
                      </button>
                    );
                  })}
                </div>
                {phase === "feedback" && chosen && (
                  <p className="ddrill-note">
                    {chosen.feedback}
                    {!chosen.correct && answer ? tx(" Better action: {label}", { label: answer.label }) : ""}
                  </p>
                )}
              </>
            )}
          </div>

          <div className="term-cta-row ddrill-cta">
            {phase === "feedback" && (
              <button
                type="button"
                className="term-continue"
                onClick={() => {
                  audio.cancelSpeech();
                  if (idx + 1 >= total) setPhase("score");
                  else {
                    setIdx((value) => value + 1);
                    setPicked(null);
                    setPhase("ask");
                  }
                }}
              >
                {idx + 1 >= total ? tx("See your result") : tx("Next situation")}
              </button>
            )}
            {phase === "score" && (
              <button type="button" className="term-continue" onClick={finish}>
                {config.continueLabel ?? tx("Continue to the check")}
              </button>
            )}
            {phase === "ask" && (
              <button type="button" className="btn term-continue term-continue--wait" disabled>
                {tx("Choose an action")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
